import { useEffect } from 'react';
import { create } from 'zustand';

/** Who owns the bottom edge on phones. The tab bar renders only when nobody
 *  does — a selection/move-mode bar REPLACES it, never overlaps (01-shell §3). */
interface BottomBarState {
  occupants: Set<string>;
  claim: (id: string) => void;
  release: (id: string) => void;
  isOccupied: () => boolean;
}

export const useBottomBarStore = create<BottomBarState>((set, get) => ({
  occupants: new Set(),
  claim: (id) => set((s) => (s.occupants.has(id) ? s : { occupants: new Set(s.occupants).add(id) })),
  release: (id) =>
    set((s) => {
      if (!s.occupants.has(id)) return s;
      const next = new Set(s.occupants);
      next.delete(id);
      return { occupants: next };
    }),
  isOccupied: () => get().occupants.size > 0,
}));

/** Claim the bottom edge while `active`; releases on unmount. */
export function useClaimBottomBar(id: string, active: boolean) {
  const claim = useBottomBarStore((s) => s.claim);
  const release = useBottomBarStore((s) => s.release);
  useEffect(() => {
    if (active) claim(id);
    else release(id);
    return () => release(id);
  }, [id, active, claim, release]);
}
